import { ShieldCheck, AlertTriangle, Key } from 'lucide-react'
import { useAuthedFetch } from '../../hooks/useAuthedFetch'
import { useTimedOut } from '../../hooks/useTimedOut'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'

interface SecurityEvent {
  event_id: string
  timestamp: string
  user_id: string
  action_type?: string
  resource_id?: string
}

export default function SecurityReports() {
  // Lambda returns { security_events: [...], count }. Direct-fetch path (see
  // hooks/useAuthedFetch) because the axios read hangs inside /admin.
  const { data, isLoading } = useAuthedFetch<{ security_events: SecurityEvent[]; count: number }>('/admin/security')

  const loadTimedOut = useTimedOut(isLoading)
  if (isLoading && !loadTimedOut) return <LoadingSkeleton rows={4} />

  // Split security events into permission changes and failed access
  const permissionChanges = data?.security_events?.filter(
    (e) => e.action_type?.includes('permission') || e.action_type?.includes('share')
  ) || []
  const failedAccess = data?.security_events?.filter(
    (e) => e.action_type?.includes('denied') || e.action_type?.includes('forbidden') || e.action_type?.includes('blocked')
  ) || []

  return (
    <div>
      <PageHeader
        title="Security Reports"
        description="Permission changes and failed access attempts across the platform — InnovateFlow administrators only."
        helpKey="admin-audit"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Permission Changes */}
        <div className="light-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Key size={18} className="text-iflow-500" />
            <h2 className="text-lg font-semibold text-gray-800">Permission Changes</h2>
          </div>
          {permissionChanges.length ? (
            <div className="space-y-3">
              {permissionChanges.map((entry) => (
                <div key={entry.event_id} className="flex items-start gap-3 py-2 border-b border-sky-200/50 last:border-0">
                  <ShieldCheck size={14} className="text-iflow-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-700">{entry.action_type}</p>
                    <p className="text-xs text-gray-500">User: {entry.user_id}</p>
                    <p className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              compact
              icon={ShieldCheck}
              title="No recent permission changes"
              description="Grants, revokes and shares will appear here as they happen."
            />
          )}
        </div>

        {/* Failed Access Attempts */}
        <div className="light-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} className="text-yellow-500" />
            <h2 className="text-lg font-semibold text-gray-800">Failed Access Attempts</h2>
          </div>
          {failedAccess.length ? (
            <div className="space-y-3">
              {failedAccess.map((entry) => (
                <div key={entry.event_id} className="flex items-start gap-3 py-2 border-b border-sky-200/50 last:border-0">
                  <AlertTriangle size={14} className="text-red-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-700">{entry.action_type}</p>
                    <p className="text-xs text-gray-500">User: {entry.user_id} | Resource: {entry.resource_id}</p>
                    <p className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              compact
              icon={AlertTriangle}
              title="No failed access attempts"
              description="Denied, forbidden and blocked requests will appear here."
            />
          )}
        </div>
      </div>

      {/* All Security Events */}
      {data?.security_events?.length ? (
        <div className="mt-6 light-card p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">All Security Events ({data.count})</h2>
          <div className="space-y-2">
            {data.security_events.map((entry) => (
              <div key={entry.event_id} className="flex items-center gap-3 py-2 border-b border-sky-200/50 last:border-0">
                <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-sky-500/10 text-gray-700 rounded-full">
                  {entry.action_type}
                </span>
                <span className="text-sm text-gray-700 flex-1">{entry.user_id}</span>
                <span className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
